import React from 'react'

import useFeatureFlags from '~/hooks/use-feature-flags'
import useToggle from '~/hooks/use-toggle'
import { i18n } from '~/i18n'
import formatCurrency from '~/utils/format-currency'

import SheerIdRemovalModalContainer from './components/sheer-id-removal-modal.container'
import {
  NormalLine,
  DiscountLine,
  Amount,
  StyledButton,
  PromotionDescription,
} from './order-item-pricing-detail-row-v3.styles'

import type { ReactNode } from 'react'

type Props = {
  description: string
  amount: number
  removeButtonEnabled?: boolean
  withPrefix?: boolean
  boldAmount?: boolean
  className?: string
  cartItemInternalId?: string
}

function prefix(amount: number): string {
  return amount < 0 ? '- ' : '+ '
}

const DiscountWrapper = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => (
  <DiscountLine className={className} data-testid="discount">
    {children}
  </DiscountLine>
)

const NormalWrapper = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return <NormalLine className={className}>{children}</NormalLine>
}

export default function OrderItemPricingDetailRow({
  description,
  amount,
  removeButtonEnabled,
  withPrefix,
  boldAmount,
  className,
  cartItemInternalId,
}: Props) {
  const { sheerIdAffiliationRemovalButtonEnabled } = useFeatureFlags()
  const [showModal, toggleModal] = useToggle(false)
  const isDiscount = amount < 0
  const Wrapper = isDiscount ? DiscountWrapper : NormalWrapper
  return (
    <Wrapper className={className}>
      <PromotionDescription title={description} data-testid="description">
        {description}
        {sheerIdAffiliationRemovalButtonEnabled && removeButtonEnabled && (
          <StyledButton key={description} onClick={toggleModal}>
            {i18n.t('components.sheer_id_removal_modal.remove_btn')}
          </StyledButton>
        )}
      </PromotionDescription>
      {showModal && (
        <SheerIdRemovalModalContainer
          cartItemInternalId={cartItemInternalId}
          toggleModal={toggleModal}
        />
      )}
      <Amount data-testid="amount" $bold={boldAmount} $discount={isDiscount}>
        {withPrefix && prefix(amount)}
        {formatCurrency(amount, {
          removeNegativePrefix: true,
        })}
      </Amount>
    </Wrapper>
  )
}
